import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Box,
  LinearProgress,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Route as RouteIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { routeService } from "../../services/routeService";
import { orderService } from "../../services/orderService";
import { userService } from "../../services/userService";

const RoutesList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load routes and medical professionals in parallel
      const [routesResponse, mpsResponse] = await Promise.all([
        routeService.getAllRoutes(),
        userService.getMedicalProfessionals(),
      ]);


      setRoutes(routesResponse || []);
      setMedicalProfessionals(mpsResponse || []);

      // FIXED: Enhance routes with medical professional info and order counts
      const enhancedRoutes = await Promise.all(
        (routesResponse || []).map(async (route) => {
          try {
            // Get order count for this route
            const orders = await routeService.getRouteOrders(route.id);

            // Find medical professional info
            const mp = (mpsResponse || []).find(
              (mp) => mp.id === route.medical_professional_id
            );
            let mpInfo = null;

            if (mp) {
              mpInfo = {
                id: mp.id,
                name: mp.user?.full_name || `MP-${mp.id}`,
                category: mp.mp_category || "Medical Professional",
                avatar_url: mp.user?.avatar_url,
                starting_location: mp.starting_location,
              };
            }

            return {
              ...route,
              order_count: orders ? orders.length : 0,
              medical_professional_info: mpInfo,
              total_orders: orders ? orders.length : 0,
              completed_orders: orders
                ? orders.filter((o) => o.status === "completed").length
                : 0,
            };
          } catch (err) {
            console.error(`Error enhancing route ${route.id}:`, err);
            return {
              ...route,
              order_count: 0,
              medical_professional_info: null,
              total_orders: 0,
              completed_orders: 0,
            };
          }
        })
      );

      setRoutes(enhancedRoutes);
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Error loading routes: " + error.message);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoutes = routes.filter((route) => {
    const routeName = route.name || "";
    const mpName = route.medical_professional_info?.name || "Unassigned";

    return (
      routeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mpName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    let color = "default";
    switch (status) {
      case "completed":
        color = "success";
        break;
      case "in_progress":
        color = "primary";
        break;
      case "assigned":
        color = "info";
        break;
      case "created":
        color = "warning";
        break;
      default:
        color = "default";
    }

    return <Chip label={status.replace("_", " ")} color={color} size="small" />;
  };

  // FIXED: Function to open route in Google Maps
  const openRouteInMaps = async (routeId) => {
    try {
      const orders = await routeService.getRouteOrders(routeId);
      if (!orders || orders.length === 0) {
        setError("No orders found in this route");
        return;
      }

      const route = routes.find((r) => r.id === routeId);
      let startPoint = "Main Medical Center, Los Angeles, CA";

      if (route?.medical_professional_info?.starting_location) {
        const loc = route.medical_professional_info.starting_location;
        if (loc.lat && loc.lng) {
          startPoint = `${loc.lat},${loc.lng}`;
        } else if (loc.address) {
          startPoint = loc.address;
        }
      }

      const validAddresses = orders
        .map((order) => order.patient_address)
        .filter(Boolean);

      if (validAddresses.length === 0) {
        setError("No valid addresses found in route orders");
        return;
      }

      // BEST APPROACH: Use the new Google Maps URL format
      const origin = encodeURIComponent(startPoint);

      if (validAddresses.length === 1) {
        const destination = encodeURIComponent(validAddresses[0]);
        const url = `https://www.google.com/maps/dir/${origin}/${destination}`;
        window.open(url, "_blank");
      } else {
        // For multiple stops, create a multi-destination URL
        const destinations = validAddresses
          .map((addr) => encodeURIComponent(addr))
          .join("/");

        const url = `https://www.google.com/maps/dir/${origin}/${destinations}`;
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error opening maps:", error);
      setError("Failed to open route in Google Maps");
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">🚗 Routes</Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/routes/optimize"
          >
            Smart Optimize
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <TextField
            placeholder="Search routes by name or medical professional"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ flexGrow: 1, mr: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {routes.length === 0 ? (
          <Box textAlign="center" p={3}>
            <RouteIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No routes found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Create routes by assigning orders to medical professionals, then
              generate routes.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RouteIcon />}
              onClick={() => navigate("/routes/optimize")}
            >
              Start Smart Route Optimization
            </Button>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Route Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Medical Professional</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Orders</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoutes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((route) => (
                    <TableRow key={route.id} hover>
                      <TableCell component="th" scope="row">
                        <Box display="flex" alignItems="center">
                          <CarIcon color="primary" sx={{ mr: 1 }} />
                          {route.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(
                          route.scheduled_date + "T00:00:00"
                        ).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {/* FIXED: Proper medical professional display */}
                        {route.medical_professional_info ? (
                          <Box display="flex" alignItems="center">
                            <Avatar
                              src={route.medical_professional_info.avatar_url}
                              sx={{
                                width: 32,
                                height: 32,
                                mr: 1,
                                bgcolor: "primary.main",
                              }}
                            >
                              {route.medical_professional_info.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {route.medical_professional_info.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {route.medical_professional_info.category}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            color="text.secondary"
                          >
                            <PersonIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">Unassigned</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(route.status)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${route.total_orders || 0} orders`}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {route.total_orders > 0 && (
                          <Box display="flex" alignItems="center">
                            <Typography variant="caption" sx={{ mr: 1 }}>
                              {route.completed_orders}/{route.total_orders}
                            </Typography>
                            <Box sx={{ width: 60 }}>
                              <LinearProgress
                                variant="determinate"
                                value={
                                  (route.completed_orders /
                                    route.total_orders) *
                                  100
                                }
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Route Details">
                          <IconButton
                            component={Link}
                            to={`/routes/${route.id}`}
                            color="primary"
                            size="small"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Route">
                          <IconButton
                            component={Link}
                            to={`/routes/${route.id}/edit`}
                            color="secondary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in Google Maps">
                          <IconButton
                            onClick={() => openRouteInMaps(route.id)}
                            color="success"
                            size="small"
                          >
                            <MapIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRoutes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default RoutesList;
